import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import './role-permissions.scss';

type PermissionLevel = 'full' | 'read' | 'none';
type RoleName =
  | 'Administrator'
  | 'Sales'
  | 'AnalysisEngineer'
  | 'Leader'
  | 'ServiceUser'
  | 'Production'
  | 'ServiceInvoicing'
  | 'SuperService'
  | 'MooringAdministrator'
  | 'MooringCalculation'
  | 'MooringSeller'
  | 'PensAdministrator'
  | 'PensSales'
  | 'PensProduction'
  | 'PensProductionSupervisor';

interface MatrixRole {
  Name: RoleName;
}

interface PermissionMatrixEntry {
  title: string;
  full?: RoleName[];
  read?: RoleName[];
  children?: PermissionMatrixEntry[];
}

interface PermissionCell {
  level: PermissionLevel;
  label: string;
}

interface PermissionRow {
  title: string;
  depth: number;
  permissions: PermissionCell[];
}

const matrixRoles: MatrixRole[] = [
  { Name: 'Administrator' },
  { Name: 'Sales' },
  { Name: 'Production' },
  { Name: 'AnalysisEngineer' },

  { Name: 'SuperService' },
  { Name: 'ServiceInvoicing' },
  { Name: 'ServiceUser' },
  
  { Name: 'MooringAdministrator' },
  { Name: 'MooringSeller' },
  { Name: 'MooringCalculation' },

  { Name: 'PensAdministrator' },
  { Name: 'PensProduction' },
  { Name: 'PensProductionSupervisor' },
  { Name: 'PensSales' },
  
];

const permissionMatrix: PermissionMatrixEntry[] = [
  { title: 'general.dashboard', full: ['Administrator', 'Sales'] },
  { title: 'general.offer', full: ['Administrator', 'Sales']},
  { title: 'general.order', full: ['Administrator', 'Sales', 'Production']},
  { title: 'analysis.title_plural', full: ['Administrator', 'AnalysisEngineer'], read: ['Sales'] },

  { title: 'general.serviceoverview', full: ['Administrator', 'SuperService', 'ServiceUser'] },
  { title: 'general.servicequeue', full: ['Administrator', 'SuperService', 'ServiceUser']},
  { title: 'reports.servicedeliveryreportheader', full: ['Administrator', 'SuperService', 'ServiceUser']},
  { title: 'reports.serviceinvoicingreportheader', full: ['Administrator', 'SuperService', 'ServiceUser']},
  { title: 'general.orderBasis', full: ['Administrator', 'SuperService','ServiceInvoicing']},

  { title: 'general.net', full: ['Administrator', 'SuperService', 'ServiceUser', 'Sales', 'Production', 'AnalysisEngineer']},
  { title: 'general.specialproduct_plural', full: ['Administrator', 'SuperService', 'ServiceUser', 'Sales', 'Production', 'AnalysisEngineer']},

  { title: 'general.customer_plural', full: ['Administrator', 'SuperService', 'ServiceUser', 'PensAdministrator', 'PensSales', 'MooringAdministrator', 'MooringSeller', 'MooringCalculation', 'Sales', 'Production', 'AnalysisEngineer']},

  { title: 'storages.storageListHeader', full: ['Administrator', 'SuperService', 'ServiceUser']},
  { title: 'storages.storageViewHeader', full: ['Administrator', 'SuperService', 'ServiceUser']},
  { title: 'storages.storageInvoiceListHeader', full: ['Administrator', 'SuperService', 'ServiceUser']},

  { title: 'mooring.offer', full: ['Administrator', 'MooringAdministrator', 'MooringSeller']},
  { title: 'mooring.offerBoard', full: ['Administrator', 'MooringAdministrator', 'MooringSeller']},
  { title: 'mooring.production', full: ['Administrator', 'MooringAdministrator', 'MooringSeller']},
  { title: 'mooring.productionBoard', full: ['Administrator', 'MooringAdministrator', 'MooringSeller']},
  { title: 'general.salesOrder', full: ['Administrator', 'MooringAdministrator', 'MooringSeller']},
  { title: 'general.certificate_plural', full: ['Administrator', 'MooringAdministrator', 'MooringSeller']},
  { title: 'mooringCalculationReport.title', full: ['Administrator', 'MooringAdministrator', 'MooringCalculation']},

  {
    title: 'pens.offer',
    full: ['Administrator', 'PensAdministrator', 'PensSales'],
    children: [
      { title: 'menu.pens', full: ['Administrator', 'PensAdministrator', 'PensSales'], read: ['PensProduction', 'PensProductionSupervisor'] },
    ],
  },
  { title: 'general.order_plural', 
    full: ['Administrator', 'PensAdministrator', 'PensSales'], 
    read: ['PensProduction', 'PensProductionSupervisor'],
    children: [
      { title: 'general.weldingLog', full: ['Administrator', 'PensAdministrator', 'PensProduction', 'PensSales'], read: ['PensProductionSupervisor'] },
      { title: 'gneral.approveWeldingLog', full: ['Administrator', 'PensAdministrator', 'PensProductionSupervisor']},
    ],
  },

  { title: 'general.administration', 
    full: ['Administrator', 'SuperService', 'PensAdministrator', 'MooringAdministrator'],
    children: [
      { title: 'general.systemadministration', full: ['Administrator', 'ServiceUser']},
      { title: 'general.statusvalue_plural', full: ['Administrator']},
      { title: 'general.dimension_plural', full: ['Administrator']},
      { title: 'general.misc', full: ['Administrator']},
      { title: 'notlogg.rope', full: ['Administrator']},
      { title: 'notlogg.looptype_plural', full: ['Administrator']},
      { title: 'notlogg.preparation', full: ['Administrator']},
      { title: 'notlogg.netting', full: ['Administrator']},
      { title: 'menu.pens', full: ['Administrator', 'PensAdministrator']},
      { title: 'notlogg.impregnation', full: ['Administrator']},
      { title: 'notlogg.service', full: ['Administrator', 'SuperService']},
      { title: 'notlogg.production', full: ['Administrator']},
      { title: 'notlogg.analysis', full: ['Administrator']},
      { title: 'general.mooring', full: ['Administrator', 'MooringAdministrator']},
      ]
    },
  { title: 'general.reports', full: ['Administrator', 'SuperService', 'ServiceUser', 'PensAdministrator', 'PensSales', 'MooringAdministrator', 'MooringSeller', 'Sales']},


];

@autoinject
export class RolePermissions {
  protected roles: MatrixRole[] = matrixRoles;
  protected rows: PermissionRow[] = [];

  constructor(private i18n: I18N) {}

  attached(): void {
    this.rows = this.createRows(permissionMatrix);
  }

  protected translateRole(roleName: string): string {
    const translationKey = `role.${roleName}`;
    const translatedRole = this.i18n.tr(translationKey);
    return translatedRole === translationKey ? roleName : translatedRole;
  }

  private createRows(entries: PermissionMatrixEntry[], depth: number = 0): PermissionRow[] {
    const rows: PermissionRow[] = [];

    for (const entry of entries) {
      rows.push({
        title: this.i18n.tr(entry.title),
        depth,
        permissions: this.roles.map((role) => this.getPermission(entry, role.Name)),
      });

      if (entry.children) {
        rows.push(...this.createRows(entry.children, depth + 1));
      }
    }

    return rows;
  }

  private getPermission(entry: PermissionMatrixEntry, roleName: RoleName): PermissionCell {
    if (entry.full?.includes(roleName)) {
      return { level: 'full', label: this.i18n.tr('rolePermissions.full') };
    }

    if (entry.read?.includes(roleName)) {
      return { level: 'read', label: this.i18n.tr('rolePermissions.read') };
    }

    return { level: 'none', label: this.i18n.tr('rolePermissions.none') };
  }
}